import { Box, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useLLMProxyServiceContext } from "contexts/llmProxySerivceContext";
import { usePreferences } from "contexts/preferences-context";
import { useEffect, useState } from "react";
import clsx from 'clsx';
import { Chat } from "./components/chat";
import { StarsLogo } from "components/starsLogo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { useUserInformationContext } from "contexts/userContext";

import SmallAIConsuption from "./components/limits-component";
import { AiUsageModal } from "components/ai-usage";

const DESKTOP_DRAWER_MIN_RATIO = 0.3;
const DESKTOP_DRAWER_DEFAULT_RATIO = 0.5;
const DESKTOP_DRAWER_MAX_RATIO = 0.75;

type AssistantPreferences = {
  assistantFullscreen: boolean,
  setAssistantFullscreen: (value: boolean) => void,
};

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    color: "white",
    backgroundColor: theme.palette.primary.main,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    borderLeft: `2px solid ${theme.palette.primary.light}`,
  },
  "@keyframes slideIn": {
    from: { transform: "translateX(100%)" },
    to: { transform: "translateX(0)" },
  },
  "@keyframes slideOut": {
    from: { transform: "translateX(0)" },
    to: { transform: "translateX(100%)" },
  },
  containerOpen: {
    animation: "$slideIn 250ms ease-in-out forwards",
  },
  containerClose: {
    animation: "$slideOut 250ms ease-in-out forwards",
  },
  header: {
    borderBottom: '1px solid #576871',
    borderTop: '1px solid #576871',
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  usage: {
    padding: "6px 10px 0px 10px",
    borderBottom: `1px solid ${theme.palette.primary.light}`,
    flexShrink: 0,
  },
  iconButton: {
    padding: "0px 12px 0px 0px"
  },
  title: {
    fontWeight: "bold",
    display: "flex",
    gap: "5px"
  },
  assistantLayout: {
    position: "fixed",
    top: 81,
    right: 0,
    height: "calc(100vh - 81px)",
    width: 448,
    maxWidth: "calc(100vw - 320px)",
    zIndex: theme.zIndex.drawer + 1,
    transform: "translateX(100%)",
    [theme.breakpoints.down("md")]: {
      top: 0,
      left: 0,
      right: 0,
      width: "100%",
      maxWidth: "none",
      height: "100dvh",
      overflowX: "hidden",
      overflowY: "hidden",
    },
  },
  resizeHandle: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: -5,
    width: 10,
    cursor: "col-resize",
    zIndex: 1,
    backgroundColor: theme.palette.primary.light,
    opacity: 0.45,
    border: 0,
    padding: 0,
    margin: 0,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      opacity: 0.5,
    },
  },
}));

export const Assistant = () => {
  const classes = useStyles();
  const {
    agentSlideOpen,
    toggleAgentSlideOpen,
    triggerClearConversation,
  } = useLLMProxyServiceContext();
  const { t } = useTranslation();
  const [initialOpen] = useState(agentSlideOpen);
  const [hasToggled, setHasToggled] = useState(false);
  const { currentUser } = useUserInformationContext();
  const { assistantFullscreen, setAssistantFullscreen } = usePreferences() as AssistantPreferences;
  const [openAIUsageDetail, setOpenAIUsageDetail] = useState<boolean>(false);
  const [drawerWidth, setDrawerWidth] = useState(() => window.innerWidth * DESKTOP_DRAWER_DEFAULT_RATIO);
  const [isResizing, setIsResizing] = useState(false);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("md"));
  const isFirstRender = !hasToggled;
  if (!hasToggled && agentSlideOpen !== initialOpen) {
    setHasToggled(true);
  }

  useEffect(() => {
    const handleWindowResize = () => {
      setDrawerWidth(window.innerWidth * DESKTOP_DRAWER_MIN_RATIO);
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const minWidth = window.innerWidth * DESKTOP_DRAWER_MIN_RATIO;
      const maxWidth = window.innerWidth * DESKTOP_DRAWER_MAX_RATIO;
      const nextWidth = Math.max(minWidth, Math.min(maxWidth, window.innerWidth - event.clientX));
      setDrawerWidth(nextWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsResizing(true);
  };

  return (
    <div
      className={clsx(classes.assistantLayout, {
        [classes.containerOpen]: !isFirstRender && agentSlideOpen,
        [classes.containerClose]: !isFirstRender && !agentSlideOpen,
      })}
      style={{
        width: (isMobile || assistantFullscreen) ? "100%" : drawerWidth,
        maxWidth: isMobile || assistantFullscreen ? "100%" : undefined,
      }}
    >
      {!isMobile && !assistantFullscreen && (
        <button
          type="button"
          aria-label={t("inference:resize-assistant")}
          className={classes.resizeHandle}
          onMouseDown={handleResizeStart}
        />
      )}
      <Box
        className={classes.container}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Box className={classes.header}>
          <Typography variant="subtitle1" className={classes.title}>
            <StarsLogo />
            {t("inference:assistant")}
          </Typography>
          <Box>
            {!isMobile && (
              <Tooltip title={assistantFullscreen ? t("inference:restoreAssistant") : t("inference:expandAssistant")}>
                <IconButton
                  color="inherit"
                  onClick={() => setAssistantFullscreen(!assistantFullscreen)}
                  className={classes.iconButton}
                  aria-label={assistantFullscreen ? t("inference:restoreAssistant") : t("inference:expandAssistant")}
                  size="large"
                >
                  <FontAwesomeIcon size="sm" icon={assistantFullscreen ? "outdent" : "expand-alt"} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t("inference:show-detail-ai")}>
              <IconButton
                color="inherit"
                onClick={() => {
                  setOpenAIUsageDetail(true);
                }}
                className={classes.iconButton}
                size="large"
              >
                <FontAwesomeIcon size="sm" icon="info-circle" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("inference:clearHistory")}>
              <IconButton
                color="inherit"
                onClick={triggerClearConversation}
                className={classes.iconButton}
                size="large"
              >
                <FontAwesomeIcon size="sm" icon="undo-alt" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("inference:close")}>
              <IconButton
                color="inherit"
                onClick={toggleAgentSlideOpen}
                className={classes.iconButton}
                size="large"
              >
                <FontAwesomeIcon size="sm" icon="times" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box className={classes.usage}>
          {currentUser?.login_email && (
            <SmallAIConsuption
              email={currentUser.login_email}
            />
          )}
        </Box>
        <Chat />
      </Box>
      {openAIUsageDetail && currentUser?.login_email && (
        <AiUsageModal
          email={currentUser.login_email}
          onClose={() => setOpenAIUsageDetail(false)}
        />
      )}
    </div>
  );
};
